import { createContext, useContext, type ReactNode } from "react";
import type { CollectionWithPrice } from "@/types/api";
import useMusioApi from "@/contexts/client/musio-client-context";
import usePaddle from "@/contexts/paddle/paddle-context";
import { useQuery } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { useCart } from "./cart-context";
import { codeIsActive, formatCurrency } from "@/utils/utils";
import usePortalConfig from "@/contexts/portal-config/portal-config-context";

type ProductContextType = {
  items: CollectionWithPrice[];
  isCartItemsPending: boolean;
  isPriceIdsFetching: boolean;
  isCartItemsError: boolean;
  isPriceError: boolean;
};

// NOTE: This is dependent on the following contexts:
// - paddle
// - api
// - cart

const ProductContext = createContext<ProductContextType>({ items: [], isCartItemsPending: true, isPriceIdsFetching: true, isCartItemsError: false, isPriceError: false });

export function ProductProvider({ children }: { children: ReactNode }) {
  const paddle = usePaddle();
  const { setCurrency } = useCart();
  const { musioApi } = useMusioApi();
  const { marketplaceDiscountId } = usePortalConfig() ?? {};

  const parsePrices = async (discountId?: string, discountStartDate?: string, discountEndDate?: string) => {
    if (!collections || !paddle) { return };

    const isValidDiscountCode = discountId != undefined && codeIsActive(discountStartDate, discountEndDate);

    // get only collections with associated cart-item (or else it doesn't have an asociated price)
    const priceIds = collections.filter(i => i.cartItem != undefined).map(i => ({ priceId: i.cartItem!.priceId, quantity: 1 }));
    const salePriceIds = collections.filter(i => i.cartItem != undefined).map(i => ({ priceId: i.cartItem!.salePriceId!, quantity: 1 }));

    // getting the price previews from paddle with the above Ids
    const [pricePreview, salePricePreview] = await Promise.all([
      priceIds.length ? paddle.PricePreview({ items: priceIds, discountId: isValidDiscountCode ? discountId : undefined }) : Promise.resolve({ data: { details: { lineItems: [] }, currencyCode: "USD", } }),
      salePriceIds.length ? paddle.PricePreview({ items: salePriceIds }) : Promise.resolve({ data: { details: { lineItems: [] } } }),
    ]);

    setCurrency(pricePreview.data.currencyCode);

    // TODO: clean this up maybe?
    const priceMap: Record<string, { formattedPrice: string; price: number }> = {};
    const salePriceMap: Record<string, { formattedSalePrice: string; salePrice: number }> = {};
    if (isValidDiscountCode) {
      pricePreview.data.details.lineItems.forEach(item => {
        const currencyCode = pricePreview.data.currencyCode;
        const price = parseInt(item.unitTotals.subtotal);
        const salePrice = price - parseInt(item.unitTotals.discount);

        priceMap[item.price.id] = {
          formattedPrice: formatCurrency(price, currencyCode), price
        };

        salePriceMap[item.price.id] = {
          formattedSalePrice: formatCurrency(salePrice, currencyCode), salePrice
        };
      });
    } else {
      pricePreview.data.details.lineItems.forEach(item => {
        priceMap[item.price.id] = {
          formattedPrice: item.formattedTotals.subtotal, price: parseFloat(item.unitTotals.subtotal)
        };
      });

      salePricePreview.data.details.lineItems.forEach(item => {
        salePriceMap[item.price.id] = { formattedSalePrice: item.formattedTotals.subtotal, salePrice: parseFloat(item.unitTotals.subtotal) };
      });
    }

    return { priceMap, salePriceMap }
  };

  // TODO: consider incremental hydration here. maybe return the prodcuts and hydrate after the products
  const { data: collections, isPending: isCartItemsPending, error: cartItemsError, isError: isCartItemsError } = musioApi.useQuery("get", "/catalog/collection");

  const { data: items = [], isPending: isPriceIdsPending, error: paddleError, isError: isPriceIdError } = useQuery({
    queryKey: ['paddle-price-ids'],
    queryFn: async () => {

      const result = await parsePrices(marketplaceDiscountId ?? undefined);
      if (result == undefined) {
        return result;
      }
      const { priceMap, salePriceMap } = result;

      // return the collection if if has a cart item
      // @ts-ignore  NOTE: the below line has a weird typing error that doesn't break anything 
      const enriched: CollectionWithPrice[] = collections.map(
        ({ cartItem, ...rest }) => ({
          ...rest,
          ...(cartItem && {
            cartItem: {
              ...cartItem,
              formattedPrice: cartItem.priceId
                ? priceMap[cartItem.priceId]?.formattedPrice
                : undefined,
              formattedSalePrice: cartItem.salePriceId
                ? salePriceMap[cartItem.salePriceId]?.formattedSalePrice
                : undefined,
              price: cartItem.priceId
                ? priceMap[cartItem.priceId]?.price
                : undefined,
              salePrice: cartItem.salePriceId
                ? salePriceMap[cartItem.salePriceId]?.salePrice
                : undefined,
            },
          }),
        })
      );

      return enriched;
    },
    enabled: collections != undefined && !(collections.length == 0) && !isCartItemsError && !isCartItemsPending, // NOTE: this is a dependent query
  });

  // INFO: error logging if present. if the service fails or paddle fails this is a big issue
  isCartItemsError && Sentry.captureException(cartItemsError.error, { data: "Captured in paddle-context.tsx" });
  isPriceIdError && Sentry.captureException(paddleError.message, { data: "Captured in paddle-context.tsx" });

  // NOTE: all cases
  // - everything loading
  // - just marketplaceItems
  // - marketplacePlaceitems and items
  const isMarketplaceItemsReady = collections != undefined && collections.length != 0 && !isCartItemsPending;
  const isItemsReady = items.length !== 0 && !isPriceIdsPending;

  const result: CollectionWithPrice[] =
    (!isMarketplaceItemsReady
      ? []
      : !isItemsReady
        ? collections.map(({ cartItem, ...rest }) => ({
          ...rest,
          ...(cartItem && {
            cartItem: {
              ...cartItem,
              formattedPrice: undefined,
              formattedSalePrice: undefined,
              price: undefined,
              salePrice: undefined,
            },
          }),
        }))
        : items).filter((collection) => collection.cartItem !== undefined); // WARN: THIS NEEDS TO BE DELETED WHEN WE INTRODUCE THE WHOLE CATALOG. THIS IS TEMPORARY!!!
  // HACK: THE ABOVE IS A HACK SEE WARN NOTE

  return (
    <ProductContext.Provider value={{
      items: result, isCartItemsPending, isPriceIdsFetching: isPriceIdsPending, isCartItemsError: isCartItemsError, isPriceError: isPriceIdError,
    }}>
      {children}
    </ProductContext.Provider>
  );
}

export function useProducts() {
  const context = useContext(ProductContext);
  if (!context) throw new Error("useProducts must be used within a ProductProvider");
  return context;
}
